import { connect } from 'react-redux';
import React, { useEffect } from 'react';
import {
    Button,
    Label,
    Link,
    Menu,
    MenuItemSwitch,
    MenuList,
    MenuPopover,
    MenuTrigger,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Spinner,
} from '@fluentui/react-components';

import {
    ArrowSyncRegular,
    ArrowSyncDismissRegular,
    CheckmarkCircleRegular,
    OptionsRegular,
    SyncOffRegular,
} from '@fluentui/react-icons';

// Component functions
import * as ComponentFunctions from './ConsumptionSyncProcess.Functions';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

// styles
import './ConsumptionSyncProcess.Styles.css';

type SwitchChangeData = {
    name: string;
    checkedItems: string[];
};

type StatusVariant = 'idle' | 'running' | 'success' | 'error';

const ConsumptionSyncProcessComponent = (props: ComponentFunctions.Props) => {
    const { t } = useTranslation();

    const [useMedicalPayableDefinitions, setUseMedicalPayableDefinitions] = React.useState<boolean>(false);

    useEffect(() => {
        if (props.protocol?.lastConsumptionSyncProcess === undefined) {
            props.GetLastProtocolConsumptionSyncProcessByProtocolId(props.ProtocolId);
        }
    }, [props.protocol?.id]);

    useEffect(() => {
        if (props.CurrentProcess) {
            setUseMedicalPayableDefinitions(props.CurrentProcess.useMedicalPayableDefinition);
        }
    }, [props.CurrentProcess]);

    // poll while running
    useEffect(() => {
        if (props.CurrentProcess?.status === 1 || props.CurrentProcess?.status === 2) {
            const interval = setInterval(() => {
                props.GetLastProtocolConsumptionSyncProcessByProtocolId(props.ProtocolId);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [props.CurrentProcess?.status]);

    useEffect(() => {
        if (props.successAdding === true && props.CurrentProcess?.status === 3) {
            props.GetAllProtocolConsumptionsByProtocol(props.ProtocolId, 1, 100, props.filters);
        }
    }, [props.successAdding && props.CurrentProcess?.status]);

    const menuCheckedValues = React.useMemo(
        () => ({ medicalPayableDefinitions: useMedicalPayableDefinitions ? ['medicalPayableDefinitionOption'] : [] }),
        [useMedicalPayableDefinitions]
    );

    const handleSwitchChange = React.useCallback((_, data: SwitchChangeData) => {
        if (data.name === 'medicalPayableDefinitions') {
            const nextValue = data.checkedItems.includes('medicalPayableDefinitionOption');
            setUseMedicalPayableDefinitions(nextValue);
        }
    }, []);

    const SyncProcessOptions = () => (
        <Menu checkedValues={menuCheckedValues} onCheckedValueChange={handleSwitchChange}>
            <MenuTrigger disableButtonEnhancement>
                <button
                    type="button"
                    className="csp-optionsBtn"
                    aria-label={t('consumptionSyncProcess.options.menuAria')}
                    title={t('consumptionSyncProcess.options.menuAria')}
                >
                    <OptionsRegular />
                </button>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    <MenuItemSwitch name="medicalPayableDefinitions" value="medicalPayableDefinitionOption">
                        <Label>{t('consumptionSyncProcess.options.enable')}</Label>
                        <p>{t('consumptionSyncProcess.options.description')}</p>
                    </MenuItemSwitch>
                </MenuList>
            </MenuPopover>
        </Menu>
    );

    // ── Derived state ────────────────────────────────────────
    const status = props.CurrentProcess?.status;
    const isAdding = !!props.isAdding;

    let variant: StatusVariant = 'idle';
    let icon: React.ReactNode = <SyncOffRegular />;
    let title: string = t('consumptionSyncProcess.status.idle');
    let canSync = true;
    let actionDisabled = false;

    if (isAdding) {
        variant = 'running';
        icon = <Spinner size="extra-tiny" />;
        title = t('consumptionSyncProcess.status.generating');
        canSync = false;
        actionDisabled = true;
    } else if (status === 1) {
        variant = 'running';
        icon = <ArrowSyncRegular />;
        title = t('consumptionSyncProcess.status.syncing');
        canSync = false;
        actionDisabled = true;
    } else if (status === 2) {
        variant = 'running';
        icon = <ArrowSyncRegular />;
        title = t('consumptionSyncProcess.status.processing');
        canSync = false;
        actionDisabled = true;
    } else if (status === 3) {
        variant = 'success';
        icon = <CheckmarkCircleRegular />;
        title = t('consumptionSyncProcess.status.success', {
            date: moment(props.CurrentProcess?.lastSync).format('DD/MM/YYYY HH:mm'),
        });
    } else if (status === 5) {
        variant = 'error';
        icon = <ArrowSyncDismissRegular />;
        title = t('consumptionSyncProcess.status.failed');
    }

    const buttonClass = `csp-syncBtn csp-syncBtn--${variant}`;

    return (
        <>
            {props.haveContractAssociated && (
                <div className="csp-container">
                    <div className={`csp-card is-${variant}`}>
                        <div className="csp-iconChip">{icon}</div>
                        <div className="csp-textBlock">
                            <span className="csp-title" title={title}>{title}</span>
                        </div>
                        {canSync && (
                            <div className="csp-actions">
                                <button
                                    type="button"
                                    className={buttonClass}
                                    disabled={actionDisabled}
                                    onClick={() =>
                                        props.CreateProtocolConsumptionSyncProcess(
                                            props.ProtocolId,
                                            useMedicalPayableDefinitions
                                        )
                                    }
                                >
                                    <span className="csp-syncBtn-iconWrap">
                                        {actionDisabled ? <Spinner size="extra-tiny" /> : <ArrowSyncRegular />}
                                    </span>
                                    <span>{t('consumptionSyncProcess.actions.sync')}</span>
                                </button>
                                <SyncProcessOptions />
                            </div>
                        )}
                    </div>

                    {!useMedicalPayableDefinitions && (
                        <MessageBar intent={'warning'}>
                            <MessageBarBody>
                                <MessageBarTitle>{t('consumptionSyncProcess.warning.title')}</MessageBarTitle>
                                {t('consumptionSyncProcess.warning.disabled')}
                            </MessageBarBody>
                        </MessageBar>
                    )}
                </div>
            )}

            {props.isLoading && !props.haveContractAssociated && (
                <MessageBar intent={'warning'}>
                    <MessageBarBody>
                        <MessageBarTitle>{t('consumptionSyncProcess.noContract.title')}</MessageBarTitle>
                        {t('consumptionSyncProcess.noContract.description')}{' '}
                        <Link href={'/contracts'}>{t('consumptionSyncProcess.noContract.link')}</Link>
                    </MessageBarBody>
                </MessageBar>
            )}

            {(props.failureLoading || props.failureAdding) && (
                <MessageBar intent={'error'}>
                    <MessageBarBody>
                        <MessageBarTitle>{t('consumptionSyncProcess.error.title')}</MessageBarTitle>
                        {props.ErrorAdding?.ErrorMessage || props.ErrorGetting?.ErrorMessage}
                    </MessageBarBody>
                </MessageBar>
            )}
        </>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ConsumptionSyncProcessComponent as any);
